/**
 * ErrorBoundary
 * React class component that catches render errors and shows a branded
 * fallback UI with retry and home navigation options.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { AlertTriangle } from 'lucide-react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary] Uncaught error:', error, info.componentStack);
    // Forward to Sentry (no-op if the SDK wasn't initialized).
    try {
      Sentry.captureException(error, {
        contexts: {
          react: { componentStack: info.componentStack ?? undefined },
        },
      });
    } catch {
      // swallow — never let the reporter break the fallback UI
    }
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="max-w-lg mx-auto text-center py-16 px-4">
        <div
          role="alert"
          className="bg-card rounded-lg shadow-lg p-8 border border-border theme-transition"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-fathom-gold/15 mb-4">
            <AlertTriangle className="w-8 h-8 text-fathom-gold" />
          </div>
          <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
            Something went wrong
          </h2>
          <p className="text-foreground-muted mb-6">
            An unexpected error occurred. You can try again or return to the
            home page.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={this.handleRetry}
              className="inline-flex items-center justify-center px-6 py-3 bg-fathom-gold text-white font-medium rounded-lg hover:bg-fathom-gold-light transition-colors"
            >
              Try Again
            </button>
            <a
              href="/"
              className="inline-flex items-center justify-center px-6 py-3 rounded-lg border border-border text-foreground font-medium hover:bg-background-tertiary transition-colors"
            >
              Go Home
            </a>
          </div>
        </div>
      </div>
    );
  }
}
